import axios from "axios";
import firebase from "./firebase";

// Define the Todo interface based on the structure used in the application

interface Todo {
  assigned_to: string;
  urgency: number;
  status: string;
  description: string;
  due_date: string;
  created_by: string;
  client_appointment_id?: string;
  creation_date_time: string;
  client_full_name?: string;
}

// Determine the base URL based on datasource
// Use PMS API when datasource is "pms-api", otherwise use general API
const TODO_BASE_URL = process.env.REACT_APP_DATA_SOURCE === "pms-api" ? "/pms/admin/todos" : "/admin/todos";

export const createTodo = async (todo: Todo) => {
  try {
    const userIdToken = await firebase.currentUser().getIdToken();
    const response = await axios.post(`${process.env.REACT_APP_API_HOST}${TODO_BASE_URL}`, todo, {
      headers: { Authorization: `Bearer ${userIdToken}` },
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Error creating todo:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};

export const updateTodo = async (todoId: string, todoData: Partial<Todo>) => {
  try {
    const userIdToken = await firebase.currentUser().getIdToken();
    return {
      success: true,
      data: await axios.patch(`${process.env.REACT_APP_API_HOST}${TODO_BASE_URL}/${todoId}`, todoData, {
        headers: { Authorization: `Bearer ${userIdToken}` },
      }),
    };
  } catch (error) {
    console.error("Error updating todo:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};

export const deleteTodo = async (todoId: string) => {
  try {
    const userIdToken = await firebase.currentUser().getIdToken();
    await axios.delete(`${process.env.REACT_APP_API_HOST}${TODO_BASE_URL}/${todoId}`, {
      headers: { Authorization: `Bearer ${userIdToken}` },
    });
    return {
      success: true,
    };
  } catch (error) {
    console.error("Error deleting todo:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};

export interface GetTodosParams {
  assigned_to?: string;
  client_appointment_id?: string;
  status?: string;
}

export interface TodoWithId extends Todo {
  id: string;
}

export const getTodos = async (
  params?: GetTodosParams
): Promise<{ success: boolean; data?: TodoWithId[]; error?: string }> => {
  try {
    const userIdToken = await firebase.currentUser().getIdToken();
    const queryParams = new URLSearchParams();

    if (params?.assigned_to) {
      queryParams.append("assigned_to", params.assigned_to);
    }
    if (params?.client_appointment_id) {
      queryParams.append("client_appointment_id", params.client_appointment_id);
    }
    if (params?.status) {
      queryParams.append("status", params.status);
    }

    const url = `${process.env.REACT_APP_API_HOST}${TODO_BASE_URL}${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${userIdToken}` },
    });

    // Handle both response formats: {data: [...]} or direct array [...]
    const todos = response.data?.data || response.data || [];

    return {
      success: true,
      data: todos,
    };
  } catch (error) {
    console.error("Error getting todos:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
      data: [],
    };
  }
};
